import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, BellOff, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { API, getApiUrl } from '../utils/api';

export const PushNotificationManager = ({ userId, compact = false }) => {
  const { t } = useTranslation();
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [supported, setSupported] = useState(true);
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);

  useEffect(() => {
    checkPushSupport();
  }, [userId]);

  const checkPushSupport = async () => {
    // Check if push is supported
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setSupported(false);
      setError(t('push.notSupported', 'Push-Benachrichtigungen werden von diesem Browser nicht unterstützt'));
      return;
    }

    // Check current permission
    setPermission(Notification.permission);

    // Wait for service worker to be ready (with retry logic)
    let retries = 0;
    const maxRetries = 10;
    
    const waitForServiceWorker = async () => {
      try {
        // First, check if there's already a controller
        if (navigator.serviceWorker.controller) {
          setServiceWorkerReady(true);
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          setIsSubscribed(!!subscription);
          return;
        }
        
        // If no controller, wait for one with timeout
        const registration = await Promise.race([
          navigator.serviceWorker.ready,
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('timeout')), 3000)
          )
        ]);
        
        setServiceWorkerReady(true);
        const subscription = await registration.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
      } catch (e) {
        retries++;
        if (retries < maxRetries) {
          // Retry after a short delay
          setTimeout(waitForServiceWorker, 1000);
        } else {
          console.error('Service Worker not available after retries');
          // Don't show error - just disable push functionality
          setServiceWorkerReady(false);
        }
      }
    };
    
    waitForServiceWorker();
  };

  const subscribeToPush = async () => {
    setLoading(true);
    setError(null);

    try {
      // Request permission
      const permissionResult = await Notification.requestPermission();
      setPermission(permissionResult);

      if (permissionResult !== 'granted') {
        setError(t('push.permissionDenied', 'Notification permission was denied'));
        setLoading(false);
        return;
      }

      // Get VAPID public key from server
      const vapidResponse = await fetch(`${getApiUrl()}/push/vapid-key`);
      if (!vapidResponse.ok) {
        throw new Error(t('push.serviceUnavailable', 'Push service unavailable'));
      }
      const { publicKey } = await vapidResponse.json();

      // Wait for service worker with timeout
      const getServiceWorkerRegistration = () => {
        return new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error(t('push.serviceWorkerTimeout', 'Service Worker not ready. Please reload the page.')));
          }, 10000); // 10 second timeout

          navigator.serviceWorker.ready.then((registration) => {
            clearTimeout(timeout);
            resolve(registration);
          }).catch(reject);
        });
      };

      const registration = await getServiceWorkerRegistration();
      
      // Convert VAPID key to Uint8Array
      const vapidPublicKey = urlBase64ToUint8Array(publicKey);

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey
      });

      // Send subscription to server
      const subscribeResponse = await fetch(`${getApiUrl()}/push/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          subscription: subscription.toJSON(),
          device_name: getDeviceName()
        })
      });

      if (!subscribeResponse.ok) {
        throw new Error(t('push.saveError', 'Error saving subscription'));
      }

      setIsSubscribed(true);
      
      // Send test notification
      await fetch(`${getApiUrl()}/push/test?user_id=${userId}`, { method: 'POST' });
      
    } catch (e) {
      console.error('Push subscription error:', e);
      setError(e.message || t('push.enableError', 'Error enabling notifications'));
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromPush = async () => {
    setLoading(true);
    setError(null);

    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
      }

      // Remove from server
      await fetch(`${getApiUrl()}/push/unsubscribe?user_id=${userId}`, {
        method: 'DELETE'
      });

      setIsSubscribed(false);
    } catch (e) {
      console.error('Push unsubscribe error:', e);
      setError(t('push.disableError', 'Error disabling notifications'));
    } finally {
      setLoading(false);
    }
  };

  // Helper to convert VAPID key
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Get device name
  const getDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows PC';
    if (/Mac/.test(ua)) return 'Mac';
    return t('push.unknownDevice', 'Unknown device');
  };

  if (!supported) {
    if (compact) return null;
    return (
      <div className="flex items-center gap-2 text-gray-500 text-sm">
        <AlertCircle className="w-4 h-4" />
        <span>{t('push.notAvailable', 'Push nicht verfügbar')}</span>
      </div>
    );
  }

  // Show loading state while waiting for service worker
  if (!serviceWorkerReady && !error) {
    if (compact) return null;
    return (
      <div className="p-4 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Bell className="w-5 h-5 text-gray-700" />
          <span className="font-medium">{t('push.title', 'Push-Benachrichtigungen')}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>{t('push.initializing', 'Wird initialisiert...')}</span>
        </div>
      </div>
    );
  }

  if (compact) {
    return (
      <Button
        variant={isSubscribed ? "outline" : "default"}
        size="sm"
        onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
        disabled={loading || permission === 'denied' || !serviceWorkerReady}
        className={isSubscribed ? "" : "bg-emerald-600 hover:bg-emerald-700"}
      >
        {loading ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
        ) : isSubscribed ? (
          <>
            <BellOff className="w-4 h-4 mr-1" />
            {t('push.off', 'Aus')}
          </>
        ) : (
          <>
            <Bell className="w-4 h-4 mr-1" />
            {t('push.on', 'An')}
          </>
        )}
      </Button>
    );
  }

  return (
    <div className="p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-gray-700" />
          <span className="font-medium">{t('push.title', 'Push-Benachrichtigungen')}</span>
        </div>
        {isSubscribed && (
          <span className="flex items-center gap-1 text-sm text-emerald-600">
            <Check className="w-4 h-4" />
            {t('push.active', 'Aktiv')}
          </span>
        )}
      </div>

      <p className="text-sm text-gray-600 mb-3">
        {t('push.description', 'Erhalte sofort eine Benachrichtigung bei neuen Nachrichten oder Tasks.')}
      </p>

      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600 mb-3">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {permission === 'denied' && (
        <div className="text-sm text-amber-600 mb-3">
          ⚠️ {t('push.blockedWarning', 'Benachrichtigungen wurden im Browser blockiert. Bitte in den Browser-Einstellungen erlauben.')}
        </div>
      )}

      <Button
        onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
        disabled={loading || permission === 'denied' || !serviceWorkerReady}
        variant={isSubscribed ? "outline" : "default"}
        className={`w-full ${!isSubscribed ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            {t('common.pleaseWait', 'Bitte warten...')}
          </>
        ) : isSubscribed ? (
          <>
            <BellOff className="w-4 h-4 mr-2" />
            {t('push.disable', 'Benachrichtigungen deaktivieren')}
          </>
        ) : (
          <>
            <Bell className="w-4 h-4 mr-2" />
            {t('push.enable', 'Benachrichtigungen aktivieren')}
          </>
        )}
      </Button>
    </div>
  );
};

export default PushNotificationManager;
